import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';

// Helper: Send to Telegram Bot API with retry logic
const sendToTelegram = async (botToken, method, payload, retries = 3) => {
  const url = `https://api.telegram.org/bot${botToken}/${method}`;
  let body;
  let headers = {};

  if (payload instanceof FormData) {
    body = payload;
  } else {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(payload);
  }

  for (let i = 0; i < retries; i++) {
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers,
        body,
      });
      const data = await res.json();
      if (!data.ok) {
        const error = new Error(data.description || 'Telegram API error');
        error.parameters = data.parameters;
        throw error;
      }
      return data;
    } catch (error) {
      if (i === retries - 1) throw error;
      await new Promise(r => setTimeout(r, 1000 * (i + 1)));
    }
  }
};

function BotDashboard() {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  
  // Bots configuration
  const [bots, setBots] = useState([]);
  const [selectedBot, setSelectedBot] = useState('');
  const [customBotToken, setCustomBotToken] = useState('');
  const [customBotName, setCustomBotName] = useState('');
  const [useCustomBot, setUseCustomBot] = useState(false);
  const [botInfo, setBotInfo] = useState(null);

  // Message type
  const [messageType, setMessageType] = useState('text');
  const [forwardChatId, setForwardChatId] = useState('');
  const [forwardMessageId, setForwardMessageId] = useState('');

  // Copy Message (instead of forward)
  const [copyMessageInstead, setCopyMessageInstead] = useState(false);

  // Text message specific
  const [textContent, setTextContent] = useState('');
  const [parseMode, setParseMode] = useState('HTML');
  const [disableWebPagePreview, setDisableWebPagePreview] = useState(true);
  
  // File upload options
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaType, setMediaType] = useState('');
  const [fileId, setFileId] = useState('');
  const [useFileId, setUseFileId] = useState(false);
  const [fileIdType, setFileIdType] = useState('document');
  const [caption, setCaption] = useState('');
  const [captionParseMode, setCaptionParseMode] = useState('HTML');
  
  // Sticker specific
  const [stickerEmoji, setStickerEmoji] = useState('');
  
  // Inline buttons
  const [inlineButtons, setInlineButtons] = useState([]);
  const [buttonType, setButtonType] = useState('url');

  // Recipients
  const [recipientsRaw, setRecipientsRaw] = useState('');
  const [recipientsList, setRecipientsList] = useState([]);

  // API Fetched IDs
  const [eduhubGroups, setEduhubGroups] = useState([]);
  const [eduhubAllIds, setEduhubAllIds] = useState({ users: [], groups: [] });
  const [neetarkChats, setNeetarkChats] = useState([]);
  const [loadingApi, setLoadingApi] = useState(false);
  const [selectedApiIds, setSelectedApiIds] = useState([]);
  const [groupInfo, setGroupInfo] = useState(null);
  const [searchGroupId, setSearchGroupId] = useState('');
  const [filterFunny, setFilterFunny] = useState('all');

  // Failed IDs tracking for retry
  const [failedIds, setFailedIds] = useState([]);
  const [retryMode, setRetryMode] = useState(false);

  // Logs & UI state
  const [logs, setLogs] = useState([]);
  const [isSending, setIsSending] = useState(false);
  const [abortController, setAbortController] = useState(null);
  const [confirmNoPhoto, setConfirmNoPhoto] = useState({ show: false, ids: [], message: null });
  const logContainerRef = useRef(null);
  const [currentProgress, setCurrentProgress] = useState({ current: 0, total: 0 });

  // Auto-scroll logs
  useEffect(() => {
    if (logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [logs]);

  // Load bots from environment
  useEffect(() => {
    const envBots = [];
    
    const neetarkToken = import.meta.env.VITE_NEETARK_TOKEN;
    if (neetarkToken && neetarkToken !== 'your_neetark_bot_token_here' && neetarkToken !== '') {
      envBots.push({
        id: 'neetark',
        name: '🤖 Neetark Bot',
        token: neetarkToken,
        apiUrl: 'https://script.google.com/macros/s/AKfycbxc9Hf1TSXEUFvvq7iodeFqlG-DhsGJRlBb9O7uTj1tPffnr4rVVh_nzNulUTGknctF/exec?action=get_chats'
      });
      addLog('✅ Loaded Neetark Bot from environment', 'success');
    }
    
    const materialToken = import.meta.env.VITE_MATERIAL_TOKEN;
    if (materialToken && materialToken !== 'your_material_bot_token_here' && materialToken !== '') {
      envBots.push({
        id: 'material',
        name: '📚 Material_eduhubkmrbot',
        token: materialToken,
        apiUrl: null
      });
      addLog('✅ Loaded Material Bot from environment', 'success');
    }
    
    const eduhubToken = import.meta.env.VITE_EDUHUBKMR_TOKEN;
    if (eduhubToken && eduhubToken !== 'your_eduhubkmr_bot_token_here' && eduhubToken !== '') {
      envBots.push({
        id: 'eduhubkmr',
        name: '🎓 EduhubKMR_bot',
        token: eduhubToken,
        groupsApi: 'https://script.google.com/macros/s/AKfycbxSYNvmeYqgp-1THtHZO7RzmD1eJ4WCWb5wxjSWI3gAXRkb35J6qqGhtmQna_dORtKS/exec?type=groups',
        allApi: 'https://script.google.com/macros/s/AKfycbxSYNvmeYqgp-1THtHZO7RzmD1eJ4WCWb5wxjSWI3gAXRkb35J6qqGhtmQna_dORtKS/exec?type=all'
      });
      addLog('✅ Loaded EduhubKMR Bot from environment', 'success');
    }
    
    if (envBots.length === 0) {
      addLog('⚠️ No bots configured. Please add bot tokens in environment variables or use custom bot.', 'warn');
      setBots([]);
    } else {
      setBots(envBots);
      setSelectedBot(envBots[0].id);
      addLog(`📦 Loaded ${envBots.length} bot(s) from configuration`, 'success');
    }
  }, []);

  // Fetch IDs when bot changes
  useEffect(() => {
    if (selectedBot === 'eduhubkmr') {
      fetchEduhubData();
    } else if (selectedBot === 'neetark') {
      fetchNeetarkData();
    } else {
      setEduhubGroups([]);
      setEduhubAllIds({ users: [], groups: [] });
      setNeetarkChats([]);
    }
  }, [selectedBot]);

  const fetchEduhubData = async () => {
    setLoadingApi(true);
    try {
      const bot = bots.find(b => b.id === 'eduhubkmr');
      if (!bot) return;
      
      const [groupsRes, allRes] = await Promise.all([
        fetch(bot.groupsApi),
        fetch(bot.allApi)
      ]);
      
      const groups = await groupsRes.json();
      const all = await allRes.json();
      
      setEduhubGroups(groups);
      setEduhubAllIds(all);
      addLog(`📊 Eduhub Data: ${groups.length} groups, ${all.users?.length || 0} users, ${all.groups?.length || 0} total groups`, 'success');
    } catch (error) {
      addLog(`❌ Failed to fetch Eduhub data: ${error.message}`, 'error');
    } finally {
      setLoadingApi(false);
    }
  };

  const fetchNeetarkData = async () => {
    setLoadingApi(true);
    try {
      const bot = bots.find(b => b.id === 'neetark');
      if (!bot || !bot.apiUrl) return;
      
      const response = await fetch(bot.apiUrl);
      const data = await response.json();
      
      if (data.success && data.chats) {
        const uniqueChats = [...new Map(data.chats.map(chat => [chat.chatId, chat])).values()];
        setNeetarkChats(uniqueChats);
        addLog(`📊 Neetark Data: ${uniqueChats.length} unique chats`, 'success');
      }
    } catch (error) {
      addLog(`❌ Failed to fetch Neetark data: ${error.message}`, 'error');
    } finally {
      setLoadingApi(false);
    }
  };

  const fetchGroupInfo = async () => {
    if (!searchGroupId.trim()) return;
    
    setLoadingApi(true);
    try {
      const bot = bots.find(b => b.id === 'eduhubkmr');
      if (!bot) return;
      
      const response = await fetch(`${bot.groupsApi}&search=${searchGroupId}`);
      const data = await response.json();
      
      if (Array.isArray(data) && data.length > 0) {
        setGroupInfo(data[0]);
        addLog(`📋 Found group: ${data[0].name}`, 'success');
      } else {
        setGroupInfo(null);
        addLog(`❌ No group found with ID: ${searchGroupId}`, 'error');
      }
    } catch (error) {
      addLog(`❌ Failed to fetch group info: ${error.message}`, 'error');
    } finally {
      setLoadingApi(false);
    }
  };

  const addApiIdsToRecipients = () => {
    let idsToAdd = [];
    
    if (selectedBot === 'eduhubkmr') {
      const selectedGroups = eduhubGroups.filter(g => selectedApiIds.includes(g.chatid));
      const allSelected = [...selectedGroups.map(g => g.chatid)];
      
      if (selectedApiIds.includes('all_users') && eduhubAllIds.users) {
        allSelected.push(...eduhubAllIds.users);
      }
      if (selectedApiIds.includes('all_groups') && eduhubAllIds.groups) {
        allSelected.push(...eduhubAllIds.groups);
      }
      idsToAdd = allSelected;
    } else if (selectedBot === 'neetark') {
      idsToAdd = neetarkChats.map(chat => chat.chatId);
    }
    
    const currentIds = recipientsRaw.split(/\r?\n/).filter(id => id.trim());
    const newIds = [...new Set([...currentIds, ...idsToAdd])];
    setRecipientsRaw(newIds.join('\n'));
    parseRecipients();
    addLog(`✅ Added ${idsToAdd.length} IDs from API`, 'success');
  };

  const testBotConnection = async () => {
    const token = getActiveBotToken();
    if (!token) {
      addLog('❌ No bot selected or token provided', 'error');
      return;
    }
    
    addLog(`🔍 Testing bot connection...`, 'info');
    
    try {
      const result = await sendToTelegram(token, 'getMe', {});
      setBotInfo(result.result);
      addLog(`✅ Connected to bot: @${result.result.username} (ID: ${result.result.id})`, 'success');
    } catch (error) {
      addLog(`❌ Bot connection failed: ${error.message}`, 'error');
      setBotInfo(null);
    }
  };

  const addLog = (message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => [...prev, { timestamp, message, type }]);
    console.log(`[${timestamp}] [${type}] ${message}`);
  };

  const parseRecipients = useCallback(() => {
    const lines = recipientsRaw.split(/\r?\n/);
    const ids = [];
    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed) continue;
      const parts = trimmed.split(/[ ,]+/);
      for (const part of parts) {
        const id = part.trim();
        if (id && /^-?\d+$/.test(id)) ids.push(id);
        else if (id) addLog(`Invalid ID format: ${id}`, 'error');
      }
    }
    const uniqueIds = [...new Set(ids)];
    setRecipientsList(uniqueIds);
    addLog(`Parsed ${uniqueIds.length} valid recipient(s)`, 'success');
    return uniqueIds;
  }, [recipientsRaw]);

  const getActiveBotToken = () => {
    if (useCustomBot && customBotToken) return customBotToken;
    const bot = bots.find(b => b.id === selectedBot);
    return bot ? bot.token : null;
  };

  const getActiveBotName = () => {
    if (useCustomBot && customBotName) return customBotName;
    const bot = bots.find(b => b.id === selectedBot);
    return bot ? bot.name : 'Unknown Bot';
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setMediaFile(file);
    setUseFileId(false);
    setFileId('');
    
    if (file.type.startsWith('image/')) setMediaType('photo');
    else if (file.type.startsWith('video/')) setMediaType('video');
    else if (file.type.startsWith('audio/')) setMediaType('audio');
    else setMediaType('document');
    addLog(`📎 File attached: ${file.name} (${(file.size / 1024).toFixed(2)} KB)`, 'info');
  };

  const buildInlineKeyboard = () => {
    if (!inlineButtons.length) return undefined;
    const keyboard = inlineButtons.map(row =>
      row.map(btn => {
        if (btn.type === 'url') return { text: btn.text, url: btn.value };
        else return { text: btn.text, callback_data: btn.value };
      })
    );
    return { inline_keyboard: keyboard };
  };

  const sendFileById = async (botToken, chatId) => {
    const replyMarkup = buildInlineKeyboard();
    const payload = {
      chat_id: chatId,
      [fileIdType]: fileId,
    };
    
    if (caption) {
      payload.caption = caption;
      if (captionParseMode) payload.parse_mode = captionParseMode;
    }
    
    if (fileIdType === 'sticker' && stickerEmoji) {
      payload.emoji = stickerEmoji;
    }
    
    if (replyMarkup) payload.reply_markup = replyMarkup;
    
    let method;
    switch (fileIdType) {
      case 'photo': method = 'sendPhoto'; break;
      case 'video': method = 'sendVideo'; break;
      case 'audio': method = 'sendAudio'; break;
      case 'document': method = 'sendDocument'; break;
      case 'sticker': method = 'sendSticker'; break;
      case 'animation': method = 'sendAnimation'; break;
      case 'voice': method = 'sendVoice'; break;
      case 'video_note': method = 'sendVideoNote'; break;
      default: method = 'sendDocument';
    }
    
    await sendToTelegram(botToken, method, payload);
  };

  const sendTextMessageToChat = async (botToken, chatId, skipMedia = false) => {
    const payload = {
      chat_id: chatId,
      text: textContent,
      disable_web_page_preview: disableWebPagePreview,
    };
    if (parseMode) payload.parse_mode = parseMode;
    const replyMarkup = buildInlineKeyboard();
    if (replyMarkup) payload.reply_markup = replyMarkup;

    // Send by File ID if enabled
    if (useFileId && fileId && !skipMedia) {
      await sendFileById(botToken, chatId);
      return;
    }

    // Send by File Upload
    if (mediaFile && !skipMedia) {
      const formData = new FormData();
      formData.append('chat_id', chatId);
      formData.append(mediaType, mediaFile);
      if (caption) {
        formData.append('caption', caption);
        if (captionParseMode) formData.append('parse_mode', captionParseMode);
      }
      if (replyMarkup) formData.append('reply_markup', JSON.stringify(replyMarkup));
      
      let method;
      switch (mediaType) {
        case 'photo': method = 'sendPhoto'; break;
        case 'video': method = 'sendVideo'; break;
        case 'audio': method = 'sendAudio'; break;
        default: method = 'sendDocument';
      }
      await sendToTelegram(botToken, method, formData);
    } else if (!skipMedia) {
      await sendToTelegram(botToken, 'sendMessage', payload);
    } else {
      await sendToTelegram(botToken, 'sendMessage', payload);
    }
  };

  const copyMessageToChat = async (botToken, chatId) => {
    const payload = {
      chat_id: chatId,
      from_chat_id: forwardChatId,
      message_id: parseInt(forwardMessageId),
    };
    // Use copyMessage API endpoint for true copy without forward tag
    return await sendToTelegram(botToken, 'copyMessage', payload);
  };

  const sendForwardMessageToChat = async (botToken, chatId) => {
    if (copyMessageInstead) {
      return await copyMessageToChat(botToken, chatId);
    } else {
      const payload = {
        chat_id: chatId,
        from_chat_id: forwardChatId,
        message_id: parseInt(forwardMessageId),
      };
      return await sendToTelegram(botToken, 'forwardMessage', payload);
    }
  };

  const sendTestToOwner = async () => {
    const ownerId = '8357186449';
    const botToken = getActiveBotToken();
    
    if (!botToken) {
      addLog('❌ No bot selected', 'error');
      return;
    }
    
    addLog(`🔍 Sending test message to owner (${ownerId})...`, 'info');
    
    try {
      if (messageType === 'text') {
        await sendTextMessageToChat(botToken, ownerId);
      } else {
        await sendForwardMessageToChat(botToken, ownerId);
      }
      addLog(`✅ Test message sent successfully to owner!`, 'success');
    } catch (error) {
      addLog(`❌ Failed to send test to owner: ${error.message}`, 'error');
    }
  };

  const executeBroadcast = async (retryFailed = false) => {
    const botToken = getActiveBotToken();
    if (!botToken) {
      addLog('❌ No valid bot token selected/provided.', 'error');
      return false;
    }

    let recipients = retryFailed ? [...failedIds] : (recipientsList.length ? recipientsList : parseRecipients());
    if (recipients.length === 0) {
      addLog('❌ No valid recipients provided.', 'error');
      return false;
    }

    if (messageType === 'text') {
      if (!textContent && !mediaFile && !(useFileId && fileId)) {
        addLog('❌ Text message requires either text content, a media file, or a file ID.', 'error');
        return false;
      }
    } else {
      if (!forwardChatId || !forwardMessageId) {
        addLog('❌ Forward message requires both Chat ID and Message ID.', 'error');
        return false;
      }
    }

    setIsSending(true);
    const controller = new AbortController();
    setAbortController(controller);
    
    const modeText = retryFailed ? `retrying ${recipients.length} failed chat(s)` : `starting broadcast to ${recipients.length} chat(s)`;
    addLog(`🚀 ${modeText} using ${getActiveBotName()}...`, 'info');

    let successCount = 0;
    let failCount = 0;
    const newFailedIds = [];
    const photoPermissionIssues = [];

    for (let i = 0; i < recipients.length; i++) {
      if (controller.signal.aborted) {
        addLog('⏹️ Broadcast aborted by user.', 'warn');
        break;
      }
      
      const chatId = recipients[i];
      const currentCount = i + 1;
      setCurrentProgress({ current: currentCount, total: recipients.length });
      
      // Show progress in console and logs
      const progressMsg = `📊 Progress: ${currentCount}/${recipients.length} - Processing ${chatId}`;
      addLog(progressMsg, 'info');
      
      if (!retryFailed && recipientsListSent.current.has(chatId)) {
        addLog(`⏭️ Skipping ${chatId} - already sent (${currentCount}/${recipients.length})`, 'info');
        continue;
      }
      
      try {
        if (messageType === 'text') {
          await sendTextMessageToChat(botToken, chatId);
        } else {
          await sendForwardMessageToChat(botToken, chatId);
        }
        successCount++;
        if (!retryFailed) recipientsListSent.current.add(chatId);
        addLog(`✅ Sent to ${chatId} (${currentCount}/${recipients.length})`, 'success');
      } catch (err) {
        failCount++;
        newFailedIds.push(chatId);
        
        if (err.message && (err.message.includes('wrong file identifier') || err.message.includes('failed to get file'))) {
          photoPermissionIssues.push(chatId);
          addLog(`⚠️ Photo permission issue for ${chatId} (${currentCount}/${recipients.length})`, 'warn');
        } else {
          addLog(`❌ Failed to send to ${chatId}: ${err.message} (${currentCount}/${recipients.length})`, 'error');
        }
      }
      
      await new Promise(r => setTimeout(r, 100));
    }

    setFailedIds(newFailedIds);
    
    // Show final summary
    addLog(`━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`, 'info');
    addLog(`📊 BROADCAST FINISHED`, 'info');
    addLog(`✅ Success: ${successCount}`, 'success');
    addLog(`❌ Failed: ${failCount}`, 'error');
    addLog(`📊 Total: ${recipients.length}`, 'info');
    
    if (newFailedIds.length > 0) {
      const failedIdsString = newFailedIds.join(', ');
      addLog(`❌ Failed IDs: ${failedIdsString}`, 'error');
      addLog(`💡 ${newFailedIds.length} recipient(s) failed. Use "Retry Failed" button to try again.`, 'warn');
    }
    addLog(`━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`, 'info');
    
    if (photoPermissionIssues.length > 0 && messageType === 'text' && (mediaFile || (useFileId && fileId && fileIdType === 'photo'))) {
      setConfirmNoPhoto({
        show: true,
        ids: photoPermissionIssues,
        message: textContent
      });
    }
    
    setIsSending(false);
    setAbortController(null);
    setCurrentProgress({ current: 0, total: 0 });
    return true;
  };

  const recipientsListSent = useRef(new Set());

  const handleSend = async () => {
    if (isSending) {
      if (abortController) abortController.abort();
      setIsSending(false);
      addLog('⏹️ Sending aborted by user.', 'warn');
      return;
    }
    recipientsListSent.current.clear();
    await executeBroadcast(false);
  };

  const handleRetryFailed = async () => {
    if (failedIds.length === 0) {
      addLog('❌ No failed recipients to retry', 'error');
      return;
    }
    await executeBroadcast(true);
  };

  const handleSendWithoutPhoto = async () => {
    if (!confirmNoPhoto.ids.length) return;
    
    setConfirmNoPhoto({ show: false, ids: [], message: null });
    addLog(`📸 Sending without photo to ${confirmNoPhoto.ids.length} chat(s)...`, 'info');
    
    const botToken = getActiveBotToken();
    const tempMediaFile = mediaFile;
    const tempUseFileId = useFileId;
    const tempFileId = fileId;
    
    setMediaFile(null);
    setUseFileId(false);
    setFileId('');
    
    let successCount = 0;
    let failCount = 0;
    const total = confirmNoPhoto.ids.length;
    
    for (let i = 0; i < confirmNoPhoto.ids.length; i++) {
      const chatId = confirmNoPhoto.ids[i];
      const currentCount = i + 1;
      
      try {
        await sendTextMessageToChat(botToken, chatId, true);
        successCount++;
        addLog(`✅ Sent text-only to ${chatId} (${currentCount}/${total})`, 'success');
      } catch (err) {
        failCount++;
        addLog(`❌ Still failed for ${chatId}: ${err.message} (${currentCount}/${total})`, 'error');
      }
      await new Promise(r => setTimeout(r, 100));
    }
    
    setMediaFile(tempMediaFile);
    setUseFileId(tempUseFileId);
    setFileId(tempFileId);
    
    addLog(`📸 Finished sending without photos`, 'success');
    addLog(`✅ Success: ${successCount}, ❌ Failed: ${failCount}`, 'info');
  };

  const handleDismissNoPhoto = () => {
    setConfirmNoPhoto({ show: false, ids: [], message: null });
    addLog(`⚠️ Skipped sending to ${confirmNoPhoto.ids.length} chat(s) with photo issues`, 'warn');
  };

  const addInlineButtonRow = () => {
    setInlineButtons([...inlineButtons, []]);
  };

  const addButtonToRow = (rowIdx, text, value) => {
    const newButtons = [...inlineButtons];
    newButtons[rowIdx].push({ text, value, type: buttonType });
    setInlineButtons(newButtons);
  };

  const removeButton = (rowIdx, btnIdx) => {
    const newButtons = [...inlineButtons];
    newButtons[rowIdx].splice(btnIdx, 1);
    if (newButtons[rowIdx].length === 0) newButtons.splice(rowIdx, 1);
    setInlineButtons(newButtons);
  };

  const clearAll = () => {
    setTextContent('');
    setMediaFile(null);
    setFileId('');
    setUseFileId(false);
    setCaption('');
    setInlineButtons([]);
    setRecipientsRaw('');
    setRecipientsList([]);
    setForwardChatId('');
    setForwardMessageId('');
    setFailedIds([]);
    recipientsListSent.current.clear();
    addLog('🧹 All fields cleared', 'info');
  };

  const addCaptionFormatting = (format) => {
    const textarea = document.getElementById('caption-textarea');
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = caption.substring(start, end);
    
    let formattedText = '';
    switch(format) {
      case 'bold':
        formattedText = `<b>${selectedText || 'bold text'}</b>`;
        break;
      case 'italic':
        formattedText = `<i>${selectedText || 'italic text'}</i>`;
        break;
      case 'underline':
        formattedText = `<u>${selectedText || 'underlined text'}</u>`;
        break;
      case 'link':
        formattedText = `<a href="https://example.com">${selectedText || 'link text'}</a>`;
        break;
      case 'code':
        formattedText = `<code>${selectedText || 'code'}</code>`;
        break;
      case 'pre':
        formattedText = `<pre>${selectedText || 'preformatted text'}</pre>`;
        break;
      case 'newline':
        formattedText = '\n';
        break;
      default:
        return;
    }
    
    const newCaption = caption.substring(0, start) + formattedText + caption.substring(end);
    setCaption(newCaption);
    
    setTimeout(() => {
      textarea.focus();
      const newCursorPos = start + formattedText.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 10);
  };

  const filteredGroups = eduhubGroups.filter(group => {
    if (filterFunny === 'allow') return group.funny === 'allow';
    if (filterFunny === 'disallow') return group.funny === 'disallow';
    return true;
  });

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-gray-100 border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <div className="text-3xl">🤖</div>
              <h1 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600">
                Telegram Broadcaster Pro
              </h1>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => navigate('/curl')}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors flex items-center gap-2 text-white"
              >
                <span>🚀</span> CURL Runner
              </button>
              <button
                onClick={logout}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg transition-colors flex items-center gap-2 text-white"
              >
                <span>🚪</span> Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Panel */}
          <div className="space-y-6">
            {/* Bot Selection */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2 text-gray-800">🤖 Bot Selection</h2>
              
              {bots.length === 0 && (
                <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-yellow-700 text-sm">⚠️ No bots configured!</p>
                  <p className="text-gray-500 text-xs mt-1">Add bot tokens or use custom bot option below.</p>
                </div>
              )}
              
              <div className="space-y-4">
                <div className="flex flex-wrap gap-4">
                  {bots.map(bot => (
                    <label key={bot.id} className="flex items-center gap-2 cursor-pointer text-gray-700">
                      <input
                        type="radio"
                        name="bot"
                        checked={selectedBot === bot.id && !useCustomBot}
                        onChange={() => { setSelectedBot(bot.id); setUseCustomBot(false); }}
                        className="w-4 h-4"
                      />
                      <span>{bot.name}</span>
                    </label>
                  ))}
                  <label className="flex items-center gap-2 cursor-pointer text-gray-700">
                    <input
                      type="radio"
                      name="bot"
                      checked={useCustomBot}
                      onChange={() => setUseCustomBot(true)}
                      className="w-4 h-4"
                    />
                    <span>➕ Custom Bot</span>
                  </label>
                </div>
                
                {useCustomBot && (
                  <div className="grid grid-cols-2 gap-3">
                    <input
                      type="text"
                      placeholder="Bot Name"
                      value={customBotName}
                      onChange={e => setCustomBotName(e.target.value)}
                      className="bg-gray-50 border border-gray-300 rounded-lg px-4 py-2 text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <input
                      type="text"
                      placeholder="Bot Token"
                      value={customBotToken}
                      onChange={e => setCustomBotToken(e.target.value)}
                      className="bg-gray-50 border border-gray-300 rounded-lg px-4 py-2 text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                )}
                
                <div className="flex gap-3">
                  <button onClick={testBotConnection} className="flex-1 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition-all duration-200">
                    🔌 Test Bot Connection
                  </button>
                  <button onClick={sendTestToOwner} className="flex-1 bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white font-semibold py-2 px-4 rounded-lg transition-all duration-200">
                    📱 Test to Owner
                  </button>
                </div>
                
                {botInfo && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm">
                    <p className="text-green-700">✅ Connected as: <strong>@{botInfo.username}</strong></p>
                    <p className="text-gray-500 text-xs mt-1">Bot ID: {botInfo.id}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Message Type */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2 text-gray-800">📨 Message Type</h2>
              <div className="flex gap-4 mb-4">
                <label className="flex items-center gap-2 cursor-pointer text-gray-700">
                  <input type="radio" value="text" checked={messageType === 'text'} onChange={() => setMessageType('text')} />
                  <span>📝 Text Message</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer text-gray-700">
                  <input type="radio" value="forward" checked={messageType === 'forward'} onChange={() => setMessageType('forward')} />
                  <span>🔄 Forward Message</span>
                </label>
              </div>

              {messageType === 'forward' && (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <input
                      type="text"
                      placeholder="From Chat ID (e.g., -100123456)"
                      value={forwardChatId}
                      onChange={e => setForwardChatId(e.target.value)}
                      className="bg-gray-50 border border-gray-300 rounded-lg px-4 py-2 text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <input
                      type="number"
                      placeholder="Message ID"
                      value={forwardMessageId}
                      onChange={e => setForwardMessageId(e.target.value)}
                      className="bg-gray-50 border border-gray-300 rounded-lg px-4 py-2 text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer text-gray-700">
                    <input
                      type="checkbox"
                      checked={copyMessageInstead}
                      onChange={e => setCopyMessageInstead(e.target.checked)}
                      className="w-4 h-4"
                    />
                    <span>📋 Copy message instead of forward (no "forwarded from" tag)</span>
                  </label>
                </div>
              )}

              {messageType === 'text' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700">Message Text</label>
                    <textarea
                      placeholder="Message text (HTML or Markdown supported)"
                      rows="4"
                      value={textContent}
                      onChange={e => setTextContent(e.target.value)}
                      className="w-full bg-gray-50 border border-gray-300 rounded-lg px-4 py-2 text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    />
                  </div>
                  
                  <div className="flex gap-3 flex-wrap">
                    <select value={parseMode} onChange={e => setParseMode(e.target.value)} className="bg-gray-50 border border-gray-300 rounded-lg px-4 py-2 text-gray-700">
                      <option value="HTML">HTML</option>
                      <option value="Markdown">Markdown</option>
                      <option value="">No formatting</option>
                    </select>
                    <label className="flex items-center gap-2 text-gray-700">
                      <input type="checkbox" checked={disableWebPagePreview} onChange={e => setDisableWebPagePreview(e.target.checked)} />
                      <span>Disable Link Preview</span>
                    </label>
                  </div>

                  {/* File Upload Section */}
                  <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <div className="flex gap-4 mb-3">
                      <label className="flex items-center gap-2 text-gray-700">
                        <input
                          type="radio"
                          checked={!useFileId}
                          onChange={() => setUseFileId(false)}
                          className="w-4 h-4"
                        />
                        <span>📁 Upload File</span>
                      </label>
                      <label className="flex items-center gap-2 text-gray-700">
                        <input
                          type="radio"
                          checked={useFileId}
                          onChange={() => setUseFileId(true)}
                          className="w-4 h-4"
                        />
                        <span>🔑 Use File ID</span>
                      </label>
                    </div>

                    {!useFileId ? (
                      <div>
                        <label className="block text-sm font-medium mb-2 text-gray-700">Upload Media (Optional)</label>
                        <input type="file" onChange={handleFileChange} className="text-sm text-gray-600" />
                        {mediaFile && (
                          <p className="text-xs text-green-600 mt-2">✅ Selected: {mediaFile.name} ({(mediaFile.size / 1024).toFixed(2)} KB)</p>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <select 
                          value={fileIdType} 
                          onChange={e => setFileIdType(e.target.value)}
                          className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2 text-gray-700"
                        >
                          <option value="document">📄 Document (PDF, ZIP, etc.)</option>
                          <option value="photo">🖼️ Photo</option>
                          <option value="video">🎥 Video</option>
                          <option value="audio">🎵 Audio</option>
                          <option value="sticker">😊 Sticker</option>
                          <option value="animation">🎬 GIF/Animation</option>
                          <option value="voice">🎤 Voice Message</option>
                          <option value="video_note">📹 Video Note</option>
                        </select>
                        
                        <input
                          type="text"
                          placeholder="Enter File ID (from Telegram)"
                          value={fileId}
                          onChange={e => setFileId(e.target.value)}
                          className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2 text-gray-800 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        
                        {fileIdType === 'sticker' && (
                          <input
                            type="text"
                            placeholder="Emoji for sticker (optional)"
                            value={stickerEmoji}
                            onChange={e => setStickerEmoji(e.target.value)}
                            className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2 text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        )}
                        
                        <p className="text-xs text-gray-500">
                          💡 How to get File ID: Send any file to @(your_bot), then forward to @getidsbot or check bot updates
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Caption Section */}
                  {(mediaFile || (useFileId && fileId)) && fileIdType !== 'sticker' && (
                    <div className="mt-2">
                      <label className="block text-sm font-medium mb-2 text-gray-700">Caption:</label>
                      <div className="flex flex-wrap gap-1 mb-2">
                        <button type="button" onClick={() => addCaptionFormatting('bold')} className="bg-gray-200 hover:bg-gray-300 px-2 py-1 rounded text-xs font-bold text-gray-700">B</button>
                        <button type="button" onClick={() => addCaptionFormatting('italic')} className="bg-gray-200 hover:bg-gray-300 px-2 py-1 rounded text-xs italic text-gray-700">I</button>
                        <button type="button" onClick={() => addCaptionFormatting('underline')} className="bg-gray-200 hover:bg-gray-300 px-2 py-1 rounded text-xs underline text-gray-700">U</button>
                        <button type="button" onClick={() => addCaptionFormatting('link')} className="bg-gray-200 hover:bg-gray-300 px-2 py-1 rounded text-xs text-gray-700">🔗 Link</button>
                        <button type="button" onClick={() => addCaptionFormatting('code')} className="bg-gray-200 hover:bg-gray-300 px-2 py-1 rounded text-xs text-gray-700">{`{ }`}</button>
                        <button type="button" onClick={() => addCaptionFormatting('pre')} className="bg-gray-200 hover:bg-gray-300 px-2 py-1 rounded text-xs text-gray-700">PRE</button>
                        <button type="button" onClick={() => addCaptionFormatting('newline')} className="bg-gray-200 hover:bg-gray-300 px-2 py-1 rounded text-xs text-gray-700">↵</button>
                      </div>
                      <textarea
                        id="caption-textarea"
                        rows="3"
                        placeholder="Write your caption here... Supports HTML tags"
                        value={caption}
                        onChange={e => setCaption(e.target.value)}
                        className="w-full bg-gray-50 border border-gray-300 rounded-lg px-4 py-2 text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none font-mono text-sm"
                      />
                      <div className="flex gap-3 mt-2">
                        <select value={captionParseMode} onChange={e => setCaptionParseMode(e.target.value)} className="bg-gray-50 border border-gray-300 rounded-lg px-3 py-1 text-sm text-gray-700">
                          <option value="HTML">HTML</option>
                          <option value="Markdown">Markdown</option>
                          <option value="">No formatting</option>
                        </select>
                        <span className="text-xs text-gray-500 self-center">{caption.length} chars</span>
                      </div>
                    </div>
                  )}

                  {/* Inline Buttons */}
                  <div className="border-t border-gray-200 pt-4">
                    <div className="flex justify-between items-center mb-3">
                      <span className="font-medium text-gray-700">Inline Buttons</span>
                      <div className="flex gap-2">
                        <select value={buttonType} onChange={e => setButtonType(e.target.value)} className="bg-gray-50 border border-gray-300 rounded px-2 py-1 text-sm text-gray-700">
                          <option value="url">URL Button</option>
                          <option value="callback">Callback Button</option>
                        </select>
                        <button onClick={addInlineButtonRow} className="bg-blue-600 hover:bg-blue-700 text-xs px-3 py-1 rounded text-white">
                          + Add Row
                        </button>
                      </div>
                    </div>
                    
                    {inlineButtons.map((row, rowIdx) => (
                      <div key={rowIdx} className="mb-3 p-3 bg-gray-50 rounded-lg">
                        <div className="flex flex-wrap gap-2 mb-2">
                          {row.map((btn, btnIdx) => (
                            <span key={btnIdx} className="bg-gray-200 px-3 py-1 rounded-full flex items-center gap-2 text-sm text-gray-700">
                              {btn.text}
                              <button onClick={() => removeButton(rowIdx, btnIdx)} className="text-red-500 hover:text-red-700">✕</button>
                            </span>
                          ))}
                        </div>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            placeholder="Button Text"
                            className="bg-white border border-gray-300 rounded-lg px-3 py-1 text-gray-700 text-sm flex-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                const text = e.target.value;
                                const value = e.target.nextSibling?.value;
                                if (text && value) addButtonToRow(rowIdx, text, value);
                                e.target.value = '';
                                if (e.target.nextSibling) e.target.nextSibling.value = '';
                              }
                            }}
                          />
                          <input
                            type="text"
                            placeholder={buttonType === 'url' ? 'https://...' : 'callback_data'}
                            className="bg-white border border-gray-300 rounded-lg px-3 py-1 text-gray-700 text-sm flex-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                const text = e.target.previousSibling?.value;
                                const value = e.target.value;
                                if (text && value) addButtonToRow(rowIdx, text, value);
                                if (e.target.previousSibling) e.target.previousSibling.value = '';
                                e.target.value = '';
                              }
                            }}
                          />
                          <button onClick={(e) => {
                            const container = e.target.parentElement;
                            const inputs = container.querySelectorAll('input');
                            const text = inputs[0].value;
                            const value = inputs[1].value;
                            if (text && value) addButtonToRow(rowIdx, text, value);
                            inputs[0].value = '';
                            inputs[1].value = '';
                          }} className="bg-green-600 text-white text-xs px-3 rounded">
                            Add
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Recipients with API Integration */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2 text-gray-800">👥 Recipients</h2>
              
              {(selectedBot === 'eduhubkmr' || selectedBot === 'neetark') && (
                <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium text-gray-700">📡 Fetch IDs from API</span>
                    <button 
                      onClick={() => selectedBot === 'eduhubkmr' ? fetchEduhubData() : fetchNeetarkData()}
                      disabled={loadingApi}
                      className="text-blue-600 hover:text-blue-700 text-sm disabled:opacity-50"
                    >
                      {loadingApi ? 'Loading...' : '🔄 Refresh'}
                    </button>
                  </div>
                  
                  {loadingApi && <div className="text-gray-500 text-sm">Loading data...</div>}
                  
                  {selectedBot === 'eduhubkmr' && eduhubGroups.length > 0 && (
                    <div className="space-y-3">
                      <div className="flex gap-2 flex-wrap">
                        <label className="flex items-center gap-2 text-sm text-gray-700">
                          <input
                            type="checkbox"
                            checked={selectedApiIds.includes('all_users')}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedApiIds([...selectedApiIds, 'all_users']);
                              } else {
                                setSelectedApiIds(selectedApiIds.filter(id => id !== 'all_users'));
                              }
                            }}
                          />
                          All Users ({eduhubAllIds.users?.length || 0})
                        </label>
                        <label className="flex items-center gap-2 text-sm text-gray-700">
                          <input
                            type="checkbox"
                            checked={selectedApiIds.includes('all_groups')}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedApiIds([...selectedApiIds, 'all_groups']);
                              } else {
                                setSelectedApiIds(selectedApiIds.filter(id => id !== 'all_groups'));
                              }
                            }}
                          />
                          All Groups ({eduhubAllIds.groups?.length || 0})
                        </label>
                      </div>
                      
                      <div className="flex gap-2">
                        <select 
                          value={filterFunny} 
                          onChange={e => setFilterFunny(e.target.value)}
                          className="bg-white border border-gray-300 rounded-lg px-3 py-1 text-sm text-gray-700"
                        >
                          <option value="all">All Groups</option>
                          <option value="allow">Funny Allowed</option>
                          <option value="disallow">Funny Disallowed</option>
                        </select>
                        <button 
                          onClick={addApiIdsToRecipients}
                          className="bg-green-600 hover:bg-green-700 text-white text-sm px-3 py-1 rounded"
                        >
                          + Add Selected to Recipients
                        </button>
                      </div>
                      
                      <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg">
                        {filteredGroups.map(group => (
                          <label key={group.chatid} className="flex items-center gap-2 p-2 hover:bg-gray-50 text-sm text-gray-700 border-b border-gray-100">
                            <input
                              type="checkbox"
                              checked={selectedApiIds.includes(group.chatid)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedApiIds([...selectedApiIds, group.chatid]);
                                } else {
                                  setSelectedApiIds(selectedApiIds.filter(id => id !== group.chatid));
                                }
                              }}
                            />
                            <span className="flex-1 truncate">{group.name}</span>
                            <span className="text-xs text-gray-400">{group.funny === 'allow' ? '😂' : '🚫'}</span>
                            <span className="text-xs text-gray-500">{group.language}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {selectedBot === 'neetark' && neetarkChats.length > 0 && (
                    <div>
                      <div className="mb-2 flex justify-between">
                        <span className="text-sm text-gray-600">{neetarkChats.length} chats available</span>
                        <button 
                          onClick={addApiIdsToRecipients}
                          className="bg-green-600 hover:bg-green-700 text-white text-sm px-3 py-1 rounded"
                        >
                          + Add All to Recipients
                        </button>
                      </div>
                      <div className="max-h-32 overflow-y-auto border border-gray-200 rounded-lg">
                        {neetarkChats.slice(0, 20).map(chat => (
                          <div key={chat.chatId} className="p-1 text-xs text-gray-500 border-b border-gray-100">
                            {chat.chatId} (added: {new Date(chat.addedOn).toLocaleDateString()})
                          </div>
                        ))}
                        {neetarkChats.length > 20 && (
                          <div className="p-1 text-xs text-gray-400 text-center">+{neetarkChats.length - 20} more</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {selectedBot === 'eduhubkmr' && (
                <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="Search Group by ID (e.g., -1002386327226)"
                      value={searchGroupId}
                      onChange={e => setSearchGroupId(e.target.value)}
                      className="flex-1 bg-white border border-gray-300 rounded-lg px-3 py-1 text-gray-700 text-sm"
                    />
                    <button onClick={fetchGroupInfo} className="bg-blue-600 hover:bg-blue-700 text-white text-sm px-3 py-1 rounded">
                      Get Info
                    </button>
                  </div>
                  {groupInfo && (
                    <div className="mt-2 p-2 bg-white rounded border border-gray-200 text-xs">
                      <p><span className="text-gray-500">Name:</span> <span className="text-gray-800">{groupInfo.name}</span></p>
                      <p><span className="text-gray-500">Funny:</span> <span className={groupInfo.funny === 'allow' ? 'text-green-600' : 'text-red-600'}>{groupInfo.funny}</span></p>
                      <p><span className="text-gray-500">Language:</span> <span className="text-gray-800">{groupInfo.language}</span></p>
                      <p><span className="text-gray-500">Correct/Incorrect:</span> <span className="text-gray-800">{groupInfo.totalCorrect}/{groupInfo.totalIncorrect}</span></p>
                      <p><span className="text-gray-500">Joined:</span> <span className="text-gray-800">{groupInfo.dateJoining}</span></p>
                    </div>
                  )}
                </div>
              )}
              
              <textarea
                placeholder="Enter Chat IDs or User IDs (one per line or comma separated)&#10;Example:&#10;123456789&#10;-1001234567890, 987654321"
                rows="5"
                value={recipientsRaw}
                onChange={e => setRecipientsRaw(e.target.value)}
                className="w-full bg-gray-50 border border-gray-300 rounded-lg px-4 py-2 text-gray-800 font-mono text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <div className="flex justify-between items-center mt-3">
                <span className="text-sm text-gray-500">Parsed: {recipientsList.length} IDs</span>
                <button onClick={parseRecipients} className="text-blue-600 hover:text-blue-700 text-sm">
                  Parse & Validate
                </button>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
              <div className="flex gap-3">
                <button onClick={clearAll} className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold py-2 px-4 rounded-lg transition-colors">
                  🧹 Clear All
                </button>
                {failedIds.length > 0 && (
                  <button onClick={handleRetryFailed} className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-white font-semibold py-2 px-4 rounded-lg transition-colors">
                    🔄 Retry Failed ({failedIds.length})
                  </button>
                )}
              </div>
            </div>

            {/* Send Button */}
            <button
              onClick={handleSend}
              disabled={!getActiveBotToken()}
              className={`w-full py-4 rounded-xl font-bold text-lg transition-all text-white ${
                isSending 
                  ? 'bg-red-600 hover:bg-red-700 animate-pulse' 
                  : 'bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700'
              } shadow-lg disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {isSending 
                ? `⏹️ Stop Broadcasting (${currentProgress.current}/${currentProgress.total})` 
                : '📤 Send Message to All Recipients'}
            </button>
          </div>

          {/* Right Panel: Logs */}
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 h-[calc(100vh-8rem)] lg:h-auto flex flex-col sticky top-24">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2 text-gray-800">📡 Live Console</h2>
            <div ref={logContainerRef} className="flex-1 bg-gray-50 rounded-lg p-4 font-mono text-xs overflow-y-auto h-96 border border-gray-200">
              {logs.length === 0 ? (
                <div className="text-gray-400 text-center mt-20">
                  No logs yet. Configure and send a broadcast.
                </div>
              ) : (
                logs.map((log, idx) => (
                  <div key={idx} className={`mb-2 pb-2 border-b border-gray-200 ${
                    log.type === 'error' ? 'text-red-600' : 
                    log.type === 'success' ? 'text-green-600' : 
                    log.type === 'warn' ? 'text-yellow-600' : 'text-gray-600'
                  }`}>
                    <span className="text-gray-400">[{log.timestamp}]</span> {log.message}
                  </div>
                ))
              )}
            </div>
            <div className="flex gap-2 mt-3">
              <button onClick={() => setLogs([])} className="text-gray-500 hover:text-gray-700 text-sm">
                Clear Logs
              </button>
            </div>
          </div>
        </div>
      </main>

      {/* Confirm Dialog for Photo Permission Issues */}
      {confirmNoPhoto.show && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-xl">
            <h3 className="text-xl font-bold mb-4 text-gray-800">⚠️ Photo Permission Issue</h3>
            <p className="text-gray-600 mb-4">
              The following {confirmNoPhoto.ids.length} chat(s) don't allow sending photos with messages.
              <br/><br/>
              Would you like to send the message without the photo to these chats?
            </p>
            <div className="max-h-32 overflow-y-auto mb-4 text-xs text-gray-500">
              {confirmNoPhoto.ids.slice(0, 10).map(id => <div key={id}>{id}</div>)}
              {confirmNoPhoto.ids.length > 10 && <div>...and {confirmNoPhoto.ids.length - 10} more</div>}
            </div>
            <div className="flex gap-3">
              <button onClick={handleSendWithoutPhoto} className="flex-1 bg-green-600 hover:bg-green-700 text-white py-2 rounded-lg">
                Yes, send without photo
              </button>
              <button onClick={handleDismissNoPhoto} className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 py-2 rounded-lg">
                No, skip these
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BotDashboard;
